import type { TrackingFn } from '@change-corgi/core-react/tracking';
import type { UtilityContext } from '@change-corgi/core-react/utilityContext';

import { getFile } from '#src/app/shared/hooks/file';
import { getNavigatorShareInterface, isShareCancellation } from '#src/app/shared/share/utils';
import { copyToClipboard } from '#src/app/shared/utils/clipboard';

import { trackShare, trackShareFailure } from './track';

type OpenTiktokEducationModalResult = {
	waitForResult: () => Promise<{ successfulShare: boolean } | undefined>;
};

type Options = {
	trackingData: Record<string, string | number | boolean | undefined>;
	trackingEvents: Record<string, string>;
	openTiktokEducationModal: (options: { onContinueAction: () => Promise<void> }) => OpenTiktokEducationModalResult;
};

export async function shareTiktok(
	data: { url: string; image: File | string | null },
	{ trackingEvents, trackingData, openTiktokEducationModal }: Options,
	track: TrackingFn,
	utilityContext: UtilityContext,
): Promise<boolean> {
	const { url } = data;

	const image = await toFile(data.image, utilityContext);

	const navigator = getNavigatorShareInterface();

	if (!image || !navigator) {
		trackShare(trackingEvents.cancel, trackingData, track);
		return false;
	}

	const triggerNativeShare = async () => {
		try {
			// not awaited: awaiting would consume the transient user gesture navigator.share needs
			void copyToClipboard(url, utilityContext.i18n);
			await navigator.share({ files: [image] });
			trackShare(trackingEvents.success, trackingData, track);
		} catch (e) {
			const error = e as Error;

			// expected user cancellation, not a genuine failure — see isShareCancellation()
			if (isShareCancellation(error)) {
				return;
			}

			void utilityContext.errorReporter.report(new Error('Could not share to tiktok', { cause: error }));

			trackShareFailure(
				trackingEvents.failure,
				{
					...trackingData,
					errorName: error.name,
					errorMessage: error.message,
				},
				track,
			);
		}
	};

	trackShare(trackingEvents.intent, trackingData, track);

	const successfulShare = await (async () => {
		try {
			// oxlint-disable-next-line typescript/no-unnecessary-condition -- navigator.canShare isn't implemented in all browsers/contexts despite the DOM lib type saying otherwise; kept per review to support older browsers explicitly rather than relying on the catch block
			if (!navigator.canShare || !navigator.canShare({ url, files: [image] })) {
				return false;
			}
			trackShare(trackingEvents.success, trackingData, track);
			const modalResult = await openTiktokEducationModal({ onContinueAction: triggerNativeShare }).waitForResult();

			return !!modalResult?.successfulShare;
		} catch (error) {
			return false;
		}
	})();

	trackShare(successfulShare ? trackingEvents.continue : trackingEvents.cancel, trackingData, track);

	return successfulShare;
}

async function toFile(file: File | string | null, utilityContext: UtilityContext): Promise<File | null> {
	if (file instanceof File) {
		return file;
	}
	if (typeof file === 'string') {
		const res = await getFile(file, undefined, true);
		if (!res) {
			void utilityContext.errorReporter.report(
				new Error(`Failed to get image file from URL for tiktok share: ${file}`),
			);
		}
		return res;
	}
	void utilityContext.errorReporter.report(new Error('Missing image for tiktok share'));
	return null;
}
